import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
import { navigation } from '../data/navigation';

type NavigationItem = {
  label: string;
  href: string;
  description?: string;
  children?: NavigationItem[];
};

const isItemActive = (pathname: string, item: NavigationItem): boolean => {
  if (pathname === item.href) return true;
  return Boolean(item.children?.some((child) => isItemActive(pathname, child)));
};

export function Header() {
  const [open, setOpen] = useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = useState<string | null>(null);
  const [expandedMobileSubItem, setExpandedMobileSubItem] = useState<string | null>(null);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setDesktopMenuOpen(false);
    setExpandedMobileItem(null);
    setExpandedMobileSubItem(null);
  }, [location.pathname]);

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(event.target as Node)) {
        setDesktopMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDesktopMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-bold transition',
      isActive ? 'bg-tint text-forest' : 'text-ink/75 hover:bg-tint/70 hover:text-forest'
    ].join(' ');

  const simpleNavigation = navigation.filter((item) => item.label !== 'Home' && item.label !== 'Contact') as NavigationItem[];

  return (
    <header className="sticky top-0 z-50 border-b border-forest/10 bg-page shadow-sm">
      <div className="mx-auto flex min-h-[84px] w-full max-w-[1540px] items-center justify-between gap-5 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Zfast Agritech India home">
          <img
            src={new URL('/images/global/zfast-logo.webp', import.meta.url).href}
            alt="ZFAST Agritech India"
            width={150}
            height={48}
            className="h-10 sm:h-12 w-auto object-contain shrink-0"
          />
          <span className="min-w-0">
            <span className="block truncate text-base font-black leading-tight tracking-tight text-ink sm:text-lg">
              ZFAST Agritech India
            </span>
            <span className="block truncate text-[0.7rem] font-semibold text-forest/75 sm:text-xs">
              Powered by Nutrition
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
          <NavLink to="/" className={desktopLinkClass}>Home</NavLink>

          {simpleNavigation.map((item) => {
            const active = isItemActive(location.pathname, item);

            if (item.children) {
              return (
                <div key={item.href} className="relative" ref={desktopMenuRef}>
                  <button
                    type="button"
                    onClick={() => setDesktopMenuOpen((value) => !value)}
                    aria-expanded={desktopMenuOpen}
                    aria-haspopup="menu"
                    className={
                      [
                        'inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-bold transition',
                        active ? 'bg-tint text-forest' : 'text-ink/75 hover:bg-tint/70 hover:text-forest'
                      ].join(' ')
                    }
                  >
                    {item.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {desktopMenuOpen && <div className="absolute left-1/2 top-full z-50 mt-3 w-56 -translate-x-1/2 rounded-[1.25rem] border border-forest/10 bg-white p-2 shadow-soft" role="menu">
                    <div className="flex flex-col gap-1">
                    {item.children.map((group) => {
                      const groupActive = isItemActive(location.pathname, group);
                      return (
                        <Link
                          key={group.href}
                          to={group.href}
                          className={[
                            'flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-black transition',
                            groupActive ? 'bg-tint text-forest' : 'text-ink/80 hover:bg-tint/70 hover:text-forest'
                          ].join(' ')}
                        >
                          <span>{group.label}</span>
                        </Link>
                      );
                    })}
                    </div>
                  </div>
                  }
                </div>
              );
            }

            return (
              <NavLink key={item.href} to={item.href} className={desktopLinkClass}>
                {item.label}
              </NavLink>
            );
          })}

          <NavLink
            to="/contact"
            className="contact-btn ml-1 rounded-full px-5 py-2.5 text-sm font-black transition"
          >
            Contact
          </NavLink>
        </nav>

        <button
          type="button"
          className="grid h-12 w-12 place-items-center text-forest transition hover:scale-105 active:scale-95 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="max-h-[calc(100svh-84px)] overflow-y-auto border-t border-forest/10 bg-page shadow-soft lg:hidden">
          <nav className="mx-auto grid w-full max-w-[1540px] gap-1 px-4 py-4 sm:px-6" aria-label="Mobile navigation">
            {(navigation as NavigationItem[]).filter((item) => item.label !== 'Contact').map((item) => {
              const active = isItemActive(location.pathname, item);
              const expanded = expandedMobileItem === item.label;

              if (item.children) {
                return (
                  <div key={item.href}>
                    <button
                      type="button"
                      className={[
                        'flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-black transition',
                        active ? 'bg-tint text-forest' : 'bg-white text-ink/75'
                      ].join(' ')}
                      onClick={() => setExpandedMobileItem(expanded ? null : item.label)}
                      aria-expanded={expanded}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={["h-4 w-4 transition", expanded ? 'rotate-180' : ''].join(' ')} />
                    </button>

                    {expanded && (
                      <div className="ml-3 mt-1 grid gap-2 border-l border-forest/10 pl-3">
                        <NavLink to={item.href} className="rounded-xl bg-tint/60 px-3 py-2 text-xs font-black text-forest" onClick={() => setOpen(false)}>
                          View all {item.label.toLowerCase()}s
                        </NavLink>
                        {item.children.map((group) => (
                            <div key={group.href} className="rounded-2xl bg-white/70 p-2">
                              <NavLink to={group.href} className="block w-full rounded-xl px-3 py-2 text-xs font-black text-ink/80 hover:bg-white" onClick={() => setOpen(false)}>
                                {group.label}
                              </NavLink>
                            </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    [
                      'block rounded-2xl px-4 py-3 text-sm font-black',
                      isActive ? 'bg-tint text-forest' : 'bg-white text-ink/75'
                    ].join(' ')
                  }
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </NavLink>
              );
            })}
            <NavLink
              to="/contact"
              className="contact-btn mt-2 rounded-2xl px-4 py-3 text-center text-sm font-black transition"
              onClick={() => setOpen(false)}
            >
              Contact
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}
